import type { NoteName, Scale } from '@tunasong/schemas'
import type { ChordType, ChordVariant } from '../chord/index.js'
import ScaleLib from '../scale/scale.js'

/**
 * Mode Lab v1's four shipped modes — Bowie-locked roster (Phase 2 Tier 2).
 *
 * Spec: docs/superpowers/specs/2026-05-18-mode-lab-design.md §3.1
 * Bowie scope memo: .claude/agent-memory/bowie/project_mode_lab_v1_scope.md
 *
 * Aeolian / Ionian are the parallel reference points (compared *against*,
 * not separate buttons); Locrian's diminished tonic destabilises the
 * "this is the key" frame and defers to v2.
 */
export type ModeLabMode = 'dorian' | 'mixolydian' | 'phrygian' | 'lydian'

/**
 * Default tonic per mode — Bowie's canonical "white-key starting at this
 * scale degree of C major" examples. Used when the user picks a new mode
 * from the segmented control (the picker resets the tonic to this default
 * for the new mode; per-mode-tonic memory deferred to v2 per §8.5).
 */
const DEFAULT_TONICS: Record<ModeLabMode, NoteName> = {
  dorian: 'D',
  mixolydian: 'G',
  phrygian: 'E',
  lydian: 'F',
}

export const getDefaultTonic = (mode: ModeLabMode): NoteName => DEFAULT_TONICS[mode]

/**
 * Parallel-reference scale type for each mode (Bowie-locked §3.2 / §8.6).
 *
 *  - Dorian / Phrygian → natural minor (Aeolian-as-default)
 *  - Mixolydian / Lydian → major (Ionian-as-default)
 *
 * The toggle MUST flip to *this* parallel — never to relative major/minor.
 * Relative comparison shares all 7 notes; parallel comparison changes
 * exactly one note and *that note is the lesson*.
 */
const PARALLEL_REFERENCE_TYPE: Record<ModeLabMode, 'major' | 'minor'> = {
  dorian: 'minor',
  phrygian: 'minor',
  mixolydian: 'major',
  lydian: 'major',
}

export const getParallelReferenceType = (mode: ModeLabMode): 'major' | 'minor' =>
  PARALLEL_REFERENCE_TYPE[mode]

interface ModeConfig {
  /**
   * 0-indexed position into the 7-note scale where the characteristic ALTERATION
   * sits — drives the glow in `<ModeScaleDisplay>`. This is the *note* that
   * differs from the parallel reference (e.g., Dorian's ♮6 — index 5).
   */
  characteristicDegreeIndex: number
  /**
   * 0-indexed root note of the mode's characteristic CHORD (always a major
   * triad in v1 per §8.3). For Mixolydian/Phrygian this equals
   * `characteristicDegreeIndex` (the characteristic note IS the chord root —
   * ♭7 is the root of ♭VII, ♭2 is the root of ♭II). For Dorian/Lydian the
   * indexes differ: the characteristic note is the 3rd of the characteristic
   * chord (♮6 is the 3rd of IV, ♯4 is the 3rd of II).
   */
  characteristicChordRootIndex: number
  /** Lexicon id for the mode's characteristic-degree "Why?" card. */
  lexiconId: string
}

const MODE_CONFIG: Record<ModeLabMode, ModeConfig> = {
  // ♮6 glows at index 5; characteristic chord IV is rooted at index 3
  dorian: { characteristicDegreeIndex: 5, characteristicChordRootIndex: 3, lexiconId: 'mode-dorian-signature' },
  // ♭7 glows at index 6; characteristic chord ♭VII is rooted at index 6 (same note)
  mixolydian: { characteristicDegreeIndex: 6, characteristicChordRootIndex: 6, lexiconId: 'mode-mixolydian-signature' },
  // ♭2 glows at index 1; characteristic chord ♭II is rooted at index 1 (same note)
  phrygian: { characteristicDegreeIndex: 1, characteristicChordRootIndex: 1, lexiconId: 'mode-phrygian-signature' },
  // ♯4 glows at index 3; characteristic chord II is rooted at index 1
  lydian: { characteristicDegreeIndex: 3, characteristicChordRootIndex: 1, lexiconId: 'mode-lydian-signature' },
}

type ChordQuality = 'major' | 'minor' | 'diminished'

const QUALITY_VARIANT: Record<ChordQuality, ChordType> = {
  major: '',
  minor: 'm',
  diminished: 'dim',
}

interface VampStep {
  /** 0-indexed into ScaleLib.getNotes(scale) — root note of this slot's chord. */
  degreeIndex: number
  quality: ChordQuality
}

/**
 * Canonical 4-chord vamps per mode (Bowie-locked §3.3). All quality calls
 * are explicit (not derived from the scale's diatonic chord-quality table)
 * so the vamp tables reads as the harmonic story: the mode's *characteristic*
 * chord is always major (IV major in Dorian, ♭VII major in Mixolydian,
 * ♭II major in Phrygian, II major in Lydian) regardless of where the diatonic
 * scale-spec might place quality nuances.
 *
 * Each step's `degreeIndex` is 0-indexed against the mode's own scale notes
 * (e.g., `[2]` in D Dorian's `[D E F G A B C]` is F).
 */
const MODE_VAMPS: Record<ModeLabMode, VampStep[]> = {
  // i – IV – i – IV
  // D Dorian: degreeIndex 0 = D, degreeIndex 3 = G → Dm – G – Dm – G
  dorian: [
    { degreeIndex: 0, quality: 'minor' },
    { degreeIndex: 3, quality: 'major' },
    { degreeIndex: 0, quality: 'minor' },
    { degreeIndex: 3, quality: 'major' },
  ],
  // I – ♭VII – IV – I
  // G Mixolydian: degreeIndex 0 = G, 6 = F, 3 = C → G – F – C – G (all major)
  mixolydian: [
    { degreeIndex: 0, quality: 'major' },
    { degreeIndex: 6, quality: 'major' },
    { degreeIndex: 3, quality: 'major' },
    { degreeIndex: 0, quality: 'major' },
  ],
  // i – ♭II – i – ♭II
  // E Phrygian: degreeIndex 0 = E, 1 = F → Em – F – Em – F
  phrygian: [
    { degreeIndex: 0, quality: 'minor' },
    { degreeIndex: 1, quality: 'major' },
    { degreeIndex: 0, quality: 'minor' },
    { degreeIndex: 1, quality: 'major' },
  ],
  // I – II – I – II
  // F Lydian: degreeIndex 0 = F, 1 = G → F – G – F – G (II is MAJOR — Lydian's signature)
  lydian: [
    { degreeIndex: 0, quality: 'major' },
    { degreeIndex: 1, quality: 'major' },
    { degreeIndex: 0, quality: 'major' },
    { degreeIndex: 1, quality: 'major' },
  ],
}

/**
 * Parallel-compare vamps per mode (Bowie §8 verdicts).
 *
 *  - Dorian → i – iv – i – iv (D minor: Dm – Gm – Dm – Gm; ♭6 → iv minor)
 *  - Mixolydian → I – V – IV – I (G major: G – D – C – G; ♭VII swaps to V)
 *  - Phrygian → i – ii° – i – ii° (E minor: Em – F#dim – Em – F#dim; §8.1
 *    Bowie verdict: keep the diminished quality — it IS part of the alteration
 *    story, ♭II major-stable ↔ ii° diminished-unstable)
 *  - Lydian → I – ii – I – ii (F major: F – Gm – F – Gm; §8.2 cleanest demo)
 *
 * Each parallel vamp is indexed against the *parallel* scale's notes — so
 * `parallelDegreeIndex 1` of E minor (E F# G A B C D) is F#, not F.
 */
const PARALLEL_VAMPS: Record<ModeLabMode, VampStep[]> = {
  dorian: [
    { degreeIndex: 0, quality: 'minor' },
    { degreeIndex: 3, quality: 'minor' },
    { degreeIndex: 0, quality: 'minor' },
    { degreeIndex: 3, quality: 'minor' },
  ],
  mixolydian: [
    { degreeIndex: 0, quality: 'major' },
    { degreeIndex: 4, quality: 'major' },
    { degreeIndex: 3, quality: 'major' },
    { degreeIndex: 0, quality: 'major' },
  ],
  phrygian: [
    { degreeIndex: 0, quality: 'minor' },
    { degreeIndex: 1, quality: 'diminished' },
    { degreeIndex: 0, quality: 'minor' },
    { degreeIndex: 1, quality: 'diminished' },
  ],
  lydian: [
    { degreeIndex: 0, quality: 'major' },
    { degreeIndex: 1, quality: 'minor' },
    { degreeIndex: 0, quality: 'major' },
    { degreeIndex: 1, quality: 'minor' },
  ],
}

const buildVamp = (steps: VampStep[], scale: Scale): ChordVariant[] => {
  const notes = ScaleLib.getNotes(scale)
  return steps.map(step => ({
    root: notes[step.degreeIndex],
    variant: QUALITY_VARIANT[step.quality],
  }))
}

/**
 * Mode Lab options per (mode, tonic) pair.
 *
 *  - `modeScale` / `parallelScale` — flips between these two on the
 *    Compare-to-parallel toggle.
 *  - `characteristicDegreeIndex` — the scale-degree position that glows in
 *    `<ModeScaleDisplay>`. SAME index works for both modeScale and
 *    parallelScale; only the pitch at that position differs.
 *  - `modeLexiconId` — the per-mode "Why?" lexicon id (e.g.,
 *    `'mode-dorian-signature'`). The Compare-to-parallel state swaps to
 *    `'mode-characteristic-note'` (generic, mode-agnostic).
 *  - `characteristicChord` — the mode's defining chord (always a major
 *    triad in v1 per §8.3: IV in Dorian, ♭VII in Mixolydian, ♭II in
 *    Phrygian, II in Lydian).
 *  - `modeVamp` / `parallelVamp` — 4-chord canonical vamps. The chord
 *    quality is explicit per slot, not derived from the diatonic scale-spec
 *    table.
 */
export interface ModeLabOptions {
  mode: ModeLabMode
  modeScale: Scale
  parallelScale: Scale
  characteristicDegreeIndex: number
  modeLexiconId: string
  characteristicChord: ChordVariant
  modeVamp: ChordVariant[]
  parallelVamp: ChordVariant[]
}

/**
 * Compute the Mode Lab data for a (mode, tonic) pair. Pure; safe to call
 * unconditionally on every render — the underlying `ScaleLib.getNotes` is
 * memoised at the scale-spec level (see `scaleNotesCache` in `scale.ts`).
 *
 * Spec §10.1 verdict: altered-Roman resolution (♭II / ♭VII / II / IV) lives
 * here in the new analyzer; we do NOT extend `ScaleLib.getChordByScaleDegree`.
 * The mode-vamp tables index the mode's own scale notes by position, then
 * apply the Bowie-locked quality per slot.
 *
 * Spec §8.4 verdict: enharmonic spelling is preserved through the analyzer
 * — the user's tonic IS the spelling reference. F# Lydian renders with F#;
 * G♭ Lydian renders with G♭. `ScaleLib.getNotes` owns the spelling logic.
 */
export const getModeLabOptions = (mode: ModeLabMode, tonic: NoteName): ModeLabOptions => {
  const config = MODE_CONFIG[mode]
  const modeScale: Scale = { root: tonic, type: mode }
  const parallelScale: Scale = { root: tonic, type: getParallelReferenceType(mode) }
  const modeNotes = ScaleLib.getNotes(modeScale)
  return {
    mode,
    modeScale,
    parallelScale,
    characteristicDegreeIndex: config.characteristicDegreeIndex,
    modeLexiconId: config.lexiconId,
    characteristicChord: {
      root: modeNotes[config.characteristicChordRootIndex],
      variant: QUALITY_VARIANT.major,
    },
    modeVamp: buildVamp(MODE_VAMPS[mode], modeScale),
    parallelVamp: buildVamp(PARALLEL_VAMPS[mode], parallelScale),
  }
}
